import * as THREE from 'three';
import { PointCloudTreeNode } from './PointCloudTree';
import { XHRFactory } from './XHRFactory';
import { Utils } from './utils';
import { numNodesLoading, maxNodesLoading, incrementNumNodesLoading, decrementNumNodesLoading } from './potreeState';

/** Interface for the loader attached to a PointCloudOctreeGeometry */
interface OctreeLoader {
  version: {
    equalOrHigher(v: string): boolean;
    upTo(v: string): boolean;
  };
  load(node: PointCloudOctreeGeometryNode): void;
}

/** Interface for point attributes */
interface PointAttributeSet {
  attributes: Array<{
    name: string;
    range: [number | number[], number | number[]];
    initialRange: [number, number];
  }>;
}

export class PointCloudOctreeGeometry {
  url: string | null;
  octreeDir: string | null;
  spacing: number;
  boundingBox: THREE.Box3 | null;
  root: PointCloudOctreeGeometryNode | null;
  nodes: Record<string, PointCloudOctreeGeometryNode> | null;
  pointAttributes: PointAttributeSet | null;
  hierarchyStepSize: number;
  loader: OctreeLoader | null;

  constructor() {
    this.url = null;
    this.octreeDir = null;
    this.spacing = 0;
    this.boundingBox = null;
    this.root = null;
    this.nodes = null;
    this.pointAttributes = null;
    this.hierarchyStepSize = -1;
    this.loader = null;
  }
}

export class PointCloudOctreeGeometryNode extends PointCloudTreeNode {
  id: number;
  name: string;
  index: number;
  pcoGeometry: PointCloudOctreeGeometry;
  geometry: THREE.BufferGeometry | null;
  boundingBox: THREE.Box3;
  boundingSphere: THREE.Sphere;
  children: Record<number, PointCloudOctreeGeometryNode>;
  numPoints: number;
  level: number | null;
  loaded: boolean;
  loading: boolean;
  oneTimeDisposeHandlers: Array<() => void>;
  parent: PointCloudOctreeGeometryNode | null;
  hasChildren: boolean;
  spacing: number;

  static IDCount = 0;

  constructor(name: string, pcoGeometry: PointCloudOctreeGeometry, boundingBox: THREE.Box3) {
    super();

    this.id = PointCloudOctreeGeometryNode.IDCount++;
    this.name = name;
    this.index = parseInt(name.charAt(name.length - 1));
    this.pcoGeometry = pcoGeometry;
    this.geometry = null;
    this.boundingBox = boundingBox;
    this.boundingSphere = boundingBox.getBoundingSphere(new THREE.Sphere());
    this.children = {};
    this.numPoints = 0;
    this.level = null;
    this.loaded = false;
    this.loading = false;
    this.oneTimeDisposeHandlers = [];
    this.parent = null;
    this.hasChildren = false;
    this.spacing = 0;
  }

  isGeometryNode() {
    return true;
  }

  getLevel() {
    return this.level;
  }

  isTreeNode() {
    return false;
  }

  isLoaded() {
    return this.loaded;
  }

  getBoundingSphere() {
    return this.boundingSphere;
  }

  getBoundingBox() {
    return this.boundingBox;
  }

  getChildren(): PointCloudOctreeGeometryNode[] {
    const children: PointCloudOctreeGeometryNode[] = [];

    for (let i = 0; i < 8; i++) {
      if (this.children[i]) {
        children.push(this.children[i]);
      }
    }

    return children;
  }

  getURL() {
    let url = '';

    const loader = this.pcoGeometry.loader;
    if (!loader) return '';

    const version = loader.version;

    if (version.equalOrHigher('1.5')) {
      url = this.pcoGeometry.octreeDir + '/' + this.getHierarchyPath() + '/' + this.name;
    } else if (version.equalOrHigher('1.4')) {
      url = this.pcoGeometry.octreeDir + '/' + this.name;
    } else if (version.upTo('1.3')) {
      url = this.pcoGeometry.octreeDir + '/' + this.name;
    }

    return url;
  }

  getHierarchyPath() {
    let path = 'r/';

    const hierarchyStepSize = this.pcoGeometry.hierarchyStepSize;
    const indices = this.name.substr(1);

    const numParts = Math.floor(indices.length / hierarchyStepSize);
    for (let i = 0; i < numParts; i++) {
      path += indices.substr(i * hierarchyStepSize, hierarchyStepSize) + '/';
    }

    path = path.slice(0, -1);

    return path;
  }

  addChild(child: PointCloudOctreeGeometryNode) {
    this.children[child.index] = child;
    child.parent = this;
  }

  load() {
    if (this.loading === true || this.loaded === true || numNodesLoading >= maxNodesLoading) {
      return;
    }

    this.loading = true;

    incrementNumNodesLoading();

    if (this.pcoGeometry.loader?.version.equalOrHigher('1.5')) {
      if ((this.level ?? 0) % this.pcoGeometry.hierarchyStepSize === 0 && this.hasChildren) {
        this.loadHierachyThenPoints();
      } else {
        this.loadPoints();
      }
    } else {
      this.loadPoints();
    }
  }

  loadPoints() {
    this.pcoGeometry.loader?.load(this);
  }

  loadHierachyThenPoints() {
    interface HierarchyStackEntry {
      children: number;
      numPoints: number;
      name: string;
    }

    // load hierarchy
    const callback = (targetNode: PointCloudOctreeGeometryNode, hbuffer: ArrayBuffer) => {
      const view = new DataView(hbuffer);

      const stack: HierarchyStackEntry[] = [];
      const children = view.getUint8(0);
      const numPoints = view.getUint32(1, true);
      targetNode.numPoints = numPoints;
      stack.push({ children: children, numPoints: numPoints, name: targetNode.name });

      const decoded: HierarchyStackEntry[] = [];

      let offset = 5;
      while (stack.length > 0) {
        const snode = stack.shift();
        if (!snode) break;
        let mask = 1;
        for (let i = 0; i < 8; i++) {
          if ((snode.children & mask) !== 0) {
            const childName = snode.name + i;

            const childChildren = view.getUint8(offset);
            const childNumPoints = view.getUint32(offset + 1, true);

            stack.push({ children: childChildren, numPoints: childNumPoints, name: childName });

            decoded.push({ children: childChildren, numPoints: childNumPoints, name: childName });

            offset += 5;
          }

          mask = mask * 2;
        }

        if (offset === hbuffer.byteLength) {
          break;
        }
      }

      const nodes: Record<string, PointCloudOctreeGeometryNode> = {};
      nodes[targetNode.name] = targetNode;
      const pco = targetNode.pcoGeometry;

      for (let i = 0; i < decoded.length; i++) {
        const name = decoded[i].name;
        const decodedNumPoints = decoded[i].numPoints;
        const index = parseInt(name.charAt(name.length - 1));
        const parentName = name.substring(0, name.length - 1);
        const parentNode = nodes[parentName];
        const level = name.length - 1;
        const boundingBox = Utils.createChildAABB(parentNode.boundingBox, index);

        const currentNode = new PointCloudOctreeGeometryNode(name, pco, boundingBox);
        currentNode.level = level;
        currentNode.numPoints = decodedNumPoints;
        currentNode.hasChildren = decoded[i].children > 0;
        currentNode.spacing = pco.spacing / Math.pow(2, level);
        parentNode.addChild(currentNode);
        nodes[name] = currentNode;
      }

      targetNode.loadPoints();
    };
    if ((this.level ?? 0) % this.pcoGeometry.hierarchyStepSize === 0) {
      const hurl = this.pcoGeometry.octreeDir + '/' + this.getHierarchyPath() + '/' + this.name + '.hrc';

      const xhr = XHRFactory.createXMLHttpRequest();
      xhr.open('GET', hurl, true);
      xhr.responseType = 'arraybuffer';
      xhr.overrideMimeType('text/plain; charset=x-user-defined');
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200 || xhr.status === 0) {
            const hbuffer = xhr.response;
            callback(this, hbuffer);
          } else {
            console.error('Failed to load file! HTTP status: ' + xhr.status + ', file: ' + hurl);
            decrementNumNodesLoading();
          }
        }
      };
      try {
        xhr.send(null);
      } catch (e) {
        console.error('fehler beim laden der punktwolke: ' + e);
      }
    }
  }

  getNumPoints() {
    return this.numPoints;
  }

  dispose() {
    if (this.geometry && this.parent !== null) {
      this.geometry.dispose();
      this.geometry = null;
      this.loaded = false;

      this.dispatchEvent({ type: 'dispose' });

      for (let i = 0; i < this.oneTimeDisposeHandlers.length; i++) {
        const handler = this.oneTimeDisposeHandlers[i];
        handler();
      }
      this.oneTimeDisposeHandlers = [];
    }
  }
}
